import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { edadEnFecha } from "@/lib/curp";

const APP_LOGO = "https://customer-assets-rejwkqb3.emergentagent.net/job_edu-censo-admin/artifacts/8c23fucn_Logo%20Censo911.png";

// Rango de edades por grado (según formato oficial)
const RANGO_POR_GRADO = {
  1: { edades: ["<6", 6, 7, 8, 9, 10], label: "PRIMER GRADO" },
  2: { edades: [6, 7, 8, 9, 10], label: "SEGUNDO GRADO" },
  3: { edades: [7, 8, 9, 10, 11], label: "TERCER GRADO" },
  4: { edades: [8, 9, 10, 11, 12], label: "CUARTO GRADO" },
  5: { edades: [9, 10, 11, 12, 13], label: "QUINTO GRADO" },
  6: { edades: [10, 11, 12, 13, 14], label: "SEXTO GRADO" },
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + (iso.includes("T") ? "" : "T12:00:00"));
  if (isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
};

// Calcula edad usando la fecha de corte oficial configurada por el Superadmin (por nivel)
const edadAlCorteFecha = (fechaNac, corteISO) => {
  if (!fechaNac || !corteISO) return "";
  const r = edadEnFecha(fechaNac, corteISO);
  return r === "" ? "" : r;
};

const nombreCompleto = (a) => [a.nombre, a.apellido_paterno, a.apellido_materno].filter(Boolean).join(" ");
const titleCase = (s) => (s || "").toLowerCase().split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

export default function DatosEstadisticosGrupo() {
  const { grupoId } = useParams();
  const { fechaCorteEdad } = useAuth();
  const [plantel, setPlantel] = useState(null);
  const [grupo, setGrupo] = useState(null);
  const [docente, setDocente] = useState(null);
  const [alumnos, setAlumnos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const g = (await api.get(`/grupos/${grupoId}`)).data;
        setGrupo(g);
        const [{ data: pls }, { data: als }] = await Promise.all([
          api.get("/planteles"),
          api.get("/alumnos", { params: { grupo_id: grupoId } }),
        ]);
        const p = pls.find((x) => x.id === g.plantel_id);
        setPlantel(p || null);
        setAlumnos(als);
        // Docente titular
        if (g.docente_id) {
          try {
            const staff = (await api.get(`/planteles/${g.plantel_id}/staff-list`)).data;
            const d = (staff.docentes || []).find((x) => x.id === g.docente_id);
            setDocente(d || null);
          } catch { /* ignore */ }
        }
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [grupoId]);

  const corteISO = fechaCorteEdad(plantel?.nivel);

  const filas = useMemo(() => {
    return alumnos
      .filter((a) => a.activo !== false)
      .sort((a, b) => nombreCompleto(a).localeCompare(nombreCompleto(b), "es"))
      .map((a, i) => ({
        no: i + 1,
        nombre: nombreCompleto(a),
        sexo: a.genero === "H" ? "H" : "M",
        status: a.activo === false ? "BAJA" : "REGULAR",
        nvo_rep: a.repetidor ? "REP" : "N.I.",
        curp: a.curp || "",
        f_nac: fmtDate(a.fecha_nacimiento),
        edad: edadAlCorteFecha(a.fecha_nacimiento, corteISO),
      }));
  }, [alumnos, corteISO]);

  const rangoGrado = RANGO_POR_GRADO[grupo?.grado] || { edades: [], label: "" };

  // Tabla estadística transpuesta: filas = categoría (H NI, H REP, M NI, M REP, SUBTOTAL); columnas = edades + TOTAL.
  const estadistica = useMemo(() => {
    const edades = rangoGrado.edades;
    const numericEdades = edades.filter((e) => typeof e === "number");
    const minE = numericEdades.length ? Math.min(...numericEdades) : 0;
    const maxE = numericEdades.length ? Math.max(...numericEdades) : 0;
    const hasMenos6 = edades.includes("<6");
    // Contadores por edad
    const counts = {};
    edades.forEach((e) => { counts[e] = { hni: 0, hrep: 0, mni: 0, mrep: 0 }; });
    filas.forEach((f) => {
      const edad = f.edad;
      let key = edades.includes(edad) ? edad : null;
      if (!key) {
        if (edad === "" || edad == null || edad < minE) key = hasMenos6 ? "<6" : minE;
        else if (edad > maxE) key = maxE;
      }
      if (key == null || !counts[key]) return;
      const isH = f.sexo === "H";
      const isRep = f.nvo_rep === "REP";
      if (isH && !isRep) counts[key].hni++;
      else if (isH && isRep) counts[key].hrep++;
      else if (!isH && !isRep) counts[key].mni++;
      else counts[key].mrep++;
    });
    // Totales por fila (suma en todas las edades)
    const rowTot = { hni: 0, hrep: 0, mni: 0, mrep: 0 };
    edades.forEach((e) => {
      rowTot.hni += counts[e].hni;
      rowTot.hrep += counts[e].hrep;
      rowTot.mni += counts[e].mni;
      rowTot.mrep += counts[e].mrep;
    });
    // Subtotal por columna (suma H+M)
    const colSubtotal = {};
    edades.forEach((e) => {
      const c = counts[e];
      colSubtotal[e] = c.hni + c.hrep + c.mni + c.mrep;
    });
    const totalGeneral = rowTot.hni + rowTot.hrep + rowTot.mni + rowTot.mrep;
    return { edades, counts, rowTot, colSubtotal, totalGeneral };
  }, [filas, rangoGrado]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6B7280]">Cargando…</div>;
  if (!grupo) return <div className="min-h-screen flex items-center justify-center text-[#9F2241]">Grupo no encontrado</div>;

  const docenteNombre = docente ? titleCase(nombreCompleto(docente)) : "____________________________________________";

  return (
    <div className="min-h-screen bg-[#EAECEE] print:bg-white p-6 print:p-0">
      <div className="mb-4 flex justify-between items-center print:hidden max-w-[800px] mx-auto">
        <div className="text-sm text-[#374151]">
          Datos Estadísticos del Grupo · {grupo.grado}° "{grupo.letra}" · {plantel?.nombre}
        </div>
        <button onClick={() => window.print()} data-testid="btn-imprimir-datos" className="px-3 py-1.5 rounded bg-[#235B4E] text-white text-sm">Imprimir / PDF</button>
      </div>

      <div className="hoja bg-white mx-auto shadow print:shadow-none p-8 print:p-0" style={{ fontFamily: "Arial, Helvetica, sans-serif", color: "#111827" }}>
        {/* Encabezado */}
        <div className="flex items-center gap-4 border-b-2 border-black pb-3">
          <img src={plantel?.logo_url || APP_LOGO} alt="Logo" className="w-16 h-16 object-contain" crossOrigin="anonymous" />
          <div className="flex-1 text-center">
            <div className="text-[14pt] font-bold uppercase" style={{ letterSpacing: "0.05em" }}>{plantel?.nombre || "—"}</div>
            <div className="text-[10pt] mt-0.5">
              C.C.T. <strong className="font-mono">{plantel?.cct || "—"}</strong> &nbsp;·&nbsp;
              TURNO <strong>{(plantel?.turno || "—").toUpperCase()}</strong>
            </div>
          </div>
          <img src={plantel?.logo_url || APP_LOGO} alt="Logo" className="w-16 h-16 object-contain" crossOrigin="anonymous" />
        </div>

        <div className="text-center mt-4">
          <div className="text-[13pt] font-bold uppercase tracking-widest">Datos Estadísticos del Grupo</div>
          <div className="text-[10pt] mt-1">
            GRADO: <strong>{grupo.grado}°</strong> &nbsp;&nbsp; GRUPO: <strong>"{grupo.letra}"</strong> &nbsp;&nbsp; CICLO: <strong>{grupo.ciclo}</strong>
          </div>
        </div>

        {/* Tabla individual */}
        <table className="w-full mt-3 border-collapse text-[8pt]">
          <thead>
            <tr className="bg-[#10312B] text-white">
              <th className="border border-black py-1 px-1 w-6">No.</th>
              <th className="border border-black py-1 px-1 text-left">NOMBRE DEL ALUMNO</th>
              <th className="border border-black py-1 px-1 w-8">SEXO</th>
              <th className="border border-black py-1 px-1 w-14">STATUS</th>
              <th className="border border-black py-1 px-1 w-10">N.I./REP.</th>
              <th className="border border-black py-1 px-1 w-24">CURP</th>
              <th className="border border-black py-1 px-1 w-16">F. NACIMIENTO</th>
              <th className="border border-black py-1 px-1 w-14">EDAD AL {corteISO ? corteISO.split("-").reverse().join("/") : ""}</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f) => (
              <tr key={f.no}>
                <td className="border border-black py-0.5 px-1 text-center">{f.no}</td>
                <td className="border border-black py-0.5 px-1 uppercase">{f.nombre}</td>
                <td className="border border-black py-0.5 px-1 text-center">{f.sexo}</td>
                <td className="border border-black py-0.5 px-1 text-center">{f.status}</td>
                <td className="border border-black py-0.5 px-1 text-center">{f.nvo_rep}</td>
                <td className="border border-black py-0.5 px-1 font-mono text-[7pt]">{f.curp}</td>
                <td className="border border-black py-0.5 px-1 text-center">{f.f_nac}</td>
                <td className="border border-black py-0.5 px-1 text-center">{f.edad}</td>
              </tr>
            ))}
            {/* Filas vacías para completar plantilla (opcional) */}
            {filas.length < 20 && Array(Math.min(20 - filas.length, 8)).fill(0).map((_, i) => (
              <tr key={`e-${i}`}>
                <td className="border border-black py-1 px-1 text-center text-[#9CA3AF]">{filas.length + i + 1}</td>
                <td className="border border-black py-1 px-1" colSpan={7}>&nbsp;</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Firma */}
        <div className="mt-8 text-center">
          <div className="w-72 mx-auto border-t border-black pt-1">
            <div className="text-[9.5pt] font-bold uppercase">{docenteNombre}</div>
            <div className="text-[8pt] uppercase tracking-widest">Profr(a). del Grupo</div>
          </div>
        </div>

        {/* Estadística por grado */}
        <div className="mt-6">
          <div className="text-center text-[11pt] font-bold uppercase tracking-widest mb-2">
            Formato Estadístico · {rangoGrado.label || `${grupo.grado}° Grado`}
          </div>
          <table className="w-full border-collapse text-[9pt] table-fixed">
            <colgroup>
              <col style={{ width: "12%" }} />
              <col style={{ width: "15%" }} />
              {estadistica.edades.map((e) => (
                <col key={`col-${e}`} style={{ width: `${61 / estadistica.edades.length}%` }} />
              ))}
              <col style={{ width: "12%" }} />
            </colgroup>
            <thead>
              <tr className="bg-[#10312B] text-white">
                <th className="border border-black py-1 px-1"></th>
                <th className="border border-black py-1 px-1"></th>
                {estadistica.edades.map((e) => (
                  <th key={String(e)} className="border border-black py-1 px-1 text-center">
                    {e === "<6" ? <>MENOS DE<br />6 AÑOS</> : String(e)}
                  </th>
                ))}
                <th className="border border-black py-1 px-1 text-center italic bg-[#235B4E]">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {/* HOMBRES */}
              <tr>
                <td rowSpan={2} className="border border-black py-1 px-1 text-center font-bold bg-[#EAECEE]">HOMBRES</td>
                <td className="border border-black py-1 px-1 font-semibold">NVO. INGRESO</td>
                {estadistica.edades.map((e) => (
                  <td key={String(e)} className="border border-black py-1 px-1 text-center">{estadistica.counts[e].hni || ""}</td>
                ))}
                <td className="border border-black py-1 px-1 text-center font-bold bg-[#F0FDF4]">{estadistica.rowTot.hni || ""}</td>
              </tr>
              <tr>
                <td className="border border-black py-1 px-1 font-semibold">REPETIDOR</td>
                {estadistica.edades.map((e) => (
                  <td key={String(e)} className="border border-black py-1 px-1 text-center">{estadistica.counts[e].hrep || ""}</td>
                ))}
                <td className="border border-black py-1 px-1 text-center font-bold bg-[#F0FDF4]">{estadistica.rowTot.hrep || ""}</td>
              </tr>
              {/* MUJERES */}
              <tr>
                <td rowSpan={2} className="border border-black py-1 px-1 text-center font-bold bg-[#EAECEE]">MUJERES</td>
                <td className="border border-black py-1 px-1 font-semibold">NVO. INGRESO</td>
                {estadistica.edades.map((e) => (
                  <td key={String(e)} className="border border-black py-1 px-1 text-center">{estadistica.counts[e].mni || ""}</td>
                ))}
                <td className="border border-black py-1 px-1 text-center font-bold bg-[#F0FDF4]">{estadistica.rowTot.mni || ""}</td>
              </tr>
              <tr>
                <td className="border border-black py-1 px-1 font-semibold">REPETIDOR</td>
                {estadistica.edades.map((e) => (
                  <td key={String(e)} className="border border-black py-1 px-1 text-center">{estadistica.counts[e].mrep || ""}</td>
                ))}
                <td className="border border-black py-1 px-1 text-center font-bold bg-[#F0FDF4]">{estadistica.rowTot.mrep || ""}</td>
              </tr>
              {/* SUBTOTAL */}
              <tr className="bg-[#EAECEE]">
                <td colSpan={2} className="border border-black py-1 px-1 text-center font-bold">SUBTOTAL</td>
                {estadistica.edades.map((e) => (
                  <td key={String(e)} className="border border-black py-1 px-1 text-center font-bold">{estadistica.colSubtotal[e] || ""}</td>
                ))}
                <td className="border border-black py-1 px-1 text-center font-bold bg-[#10312B] text-white">{estadistica.totalGeneral || ""}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        @page { size: Letter portrait; margin: 12mm 14mm; }
        .hoja { width: 190mm; box-sizing: border-box; }
        @media print {
          html, body { margin: 0 !important; padding: 0 !important; background: white; }
          .hoja { box-shadow: none !important; padding: 0 !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>
    </div>
  );
}
